import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MediaImageField from "@/components/common/MediaImageField";

const EMPTY = {
  meta_title: "",
  meta_description: "",
  keywords: [],
  canonical_url: "",
  robots: "index,follow",
  og_title: "",
  og_description: "",
  og_image: "",
  og_type: "website",
  schema: "",
};

function Field({ label, children, hint, warn }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-white/50">{label}</Label>
      {children}
      {warn && <p className="text-[11px] text-amber-200/80">{warn}</p>}
      {hint && <p className="text-[11px] text-white/40 leading-relaxed">{hint}</p>}
    </div>
  );
}

function ChoiceRow({ value, options, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-3 h-9 rounded-lg text-sm border transition-colors ${
              on
                ? "bg-white/12 text-white border-cyan-400/40"
                : "text-white/55 border-white/10 hover:text-white hover:border-white/25"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function lenWarn(len, min, max) {
  const ok = len >= min && len <= max;
  return {
    ok,
    text: `${len} characters · aim ${min}–${max}`,
  };
}

/**
 * Full CMS SEO editor (flat doc.seo shape).
 * value: seo object; onChange(nextSeo); defaults for og_type / folder.
 */
export default function SeoFields({
  value,
  onChange,
  ogTypeDefault = "website",
  mediaFolder = "uploads",
  compact = false,
}) {
  const seo = { ...EMPTY, og_type: ogTypeDefault, ...(value || {}) };
  const set = (key, v) => onChange({ ...seo, [key]: v });
  const titleLen = lenWarn((seo.meta_title || "").length, 40, 60);
  const descLen = lenWarn((seo.meta_description || "").length, 120, 160);
  const kw = Array.isArray(seo.keywords) ? seo.keywords.join(", ") : (seo.keywords || "");

  return (
    <div className={`space-y-4 ${compact ? "" : "rounded-xl border border-white/10 bg-white/[0.02] p-4"}`}>
      {!compact && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-200/70 font-medium">Search &amp; social SEO</p>
          <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
            These fields control what Google and social apps show. Preview updates as you type; live site updates after you save (and publish in SEO if used).
          </p>
        </div>
      )}

      <p className="text-[11px] uppercase tracking-wider text-white/45">Search (Google)</p>
      <Field label="Google title" warn={!titleLen.ok && seo.meta_title ? titleLen.text : null} hint={titleLen.ok || !seo.meta_title ? titleLen.text : "Leave blank to keep the Google title already published for this page."}>
        <Input
          value={seo.meta_title || ""}
          onChange={(e) => set("meta_title", e.target.value)}
          placeholder="Clear title for Google results"
          className={`bg-black/20 text-white placeholder:text-white/30 ${!titleLen.ok && seo.meta_title ? "border-amber-400/50" : "border-white/10"}`}
        />
      </Field>
      <Field label="Google description" warn={!descLen.ok && seo.meta_description ? descLen.text : null} hint={descLen.text}>
        <Textarea
          value={seo.meta_description || ""}
          onChange={(e) => set("meta_description", e.target.value)}
          rows={3}
          placeholder="One or two sentences that earn the click"
          className={`bg-black/20 text-white placeholder:text-white/30 ${!descLen.ok && seo.meta_description ? "border-amber-400/50" : "border-white/10"}`}
        />
      </Field>
      <Field label="Keywords" hint="Optional. Comma-separated. First is the primary focus.">
        <Input
          value={kw}
          onChange={(e) => set("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder="primary topic, related phrase"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
        />
      </Field>
      <Field label="Canonical URL" hint="Leave blank for the default page URL.">
        <Input
          value={seo.canonical_url || ""}
          onChange={(e) => set("canonical_url", e.target.value)}
          placeholder="https://…/path"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
        />
      </Field>
      <Field label="Indexing" hint="Most pages should stay on Show in Google.">
        <ChoiceRow
          value={seo.robots || "index,follow"}
          onChange={(v) => set("robots", v)}
          options={[
            { label: "Show in Google", value: "index,follow" },
            { label: "Hide from Google", value: "noindex,follow" },
            { label: "Hide + no follow", value: "noindex,nofollow" },
          ]}
        />
      </Field>

      <p className="text-[11px] uppercase tracking-wider text-white/45 pt-2 border-t border-white/10">Social share</p>
      <Field label="Share title" hint="Defaults to Google title if empty.">
        <Input
          value={seo.og_title || ""}
          onChange={(e) => set("og_title", e.target.value)}
          placeholder="Optional share title"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
        />
      </Field>
      <Field label="Share description" hint="Defaults to Google description if empty.">
        <Textarea
          value={seo.og_description || ""}
          onChange={(e) => set("og_description", e.target.value)}
          rows={2}
          placeholder="Optional share blurb"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
        />
      </Field>
      <Field label="Share image" hint="Ideal ~1200×630.">
        <MediaImageField
          value={seo.og_image || ""}
          onChange={(url) => set("og_image", url)}
          folder={mediaFolder}
          aspectClass="h-28"
        />
      </Field>
      <Field label="Share type">
        <ChoiceRow
          value={seo.og_type || ogTypeDefault}
          onChange={(v) => set("og_type", v)}
          options={[
            { label: "Website", value: "website" },
            { label: "Article", value: "article" },
          ]}
        />
      </Field>

      <p className="text-[11px] uppercase tracking-wider text-white/45 pt-2 border-t border-white/10">Advanced</p>
      <Field label="Structured data (JSON-LD)" hint="Optional. Paste valid JSON only.">
        <Textarea
          value={typeof seo.schema === "string" ? seo.schema : (seo.schema ? JSON.stringify(seo.schema, null, 2) : "")}
          onChange={(e) => set("schema", e.target.value)}
          rows={5}
          placeholder='{"@context":"https://schema.org","@type":"WebPage"}'
          className="bg-black/40 border-white/10 text-white font-mono text-xs placeholder:text-white/30"
        />
      </Field>
    </div>
  );
}

export { EMPTY as EMPTY_SEO };
